import React, { Fragment, useEffect, useMemo, useState } from "react";
import axios from "axios";
import swal from "sweetalert";
import { useTranslation } from "react-i18next";

/* =========================================================
   CONFIG
========================================================= */

const API_KEY =
  "eyJ0eXAiOiJKV1Qidfg%!#%$%LCdf#*&*JhbGciOiJSUzI1NiJ9";

/* =========================================================
   HELPERS
========================================================= */

const getErrorMessage = (errorValue) => {
  if (!errorValue) return "";

  if (Array.isArray(errorValue)) {
    return errorValue[0] || "";
  }

  if (typeof errorValue === "string") {
    return errorValue;
  }

  return "";
};

const safeText = (value, fallback = "—") => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  return value;
};

const formatNumber = (value) => {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return "0";
  }

  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(number);
};

/* =========================================================
   ICONS
========================================================= */

const WithdrawIcon = ({ type }) => {
  const common = {
    width: 22,
    height: 22,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };

  if (type === "method") {
    return (
      <svg {...common}>
        <rect x="3" y="5" width="18" height="14" rx="3" />
        <path d="M3 10h18" />
        <path d="M8 15h3" />
      </svg>
    );
  }

  if (type === "amount") {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="9" />
        <path d="M16 8.5h-5a2 2 0 0 0 0 4h2a2 2 0 0 1 0 4H8" />
        <path d="M12 6.5v2" />
        <path d="M12 16.5v2" />
      </svg>
    );
  }

  if (type === "receiver") {
    return (
      <svg {...common}>
        <rect x="4" y="3" width="16" height="18" rx="3" />
        <path d="M9 7h6" />
        <path d="M8 12h8" />
        <path d="M8 16h5" />
      </svg>
    );
  }

  if (type === "password") {
    return (
      <svg {...common}>
        <rect x="5" y="10" width="14" height="10" rx="2" />
        <path d="M8 10V7a4 4 0 0 1 8 0v3" />
        <path d="M12 14v2" />
      </svg>
    );
  }

  if (type === "shield") {
    return (
      <svg {...common}>
        <path d="M12 3 20 6v5c0 5-3.4 8.5-8 10-4.6-1.5-8-5-8-10V6l8-3Z" />
        <path d="m9 12 2 2 4-4" />
      </svg>
    );
  }

  return (
    <svg {...common}>
      <path d="M12 3v12" />
      <path d="m7 10 5 5 5-5" />
      <path d="M5 21h14" />
    </svg>
  );
};

/* =========================================================
   MAIN COMPONENT
========================================================= */

const Checkout = () => {
  const { t } = useTranslation();

  const [withdrawMethods, setWithdrawMethods] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [withdrawInput, setWithdrawInput] = useState({
    gateway_id: "",
    amount: "",
    transaction_number: "",
    password: "",
    w_select: "",
    error_list: {},
  });

  /* =========================================================
     LOAD WITHDRAW METHODS
  ========================================================= */

  useEffect(() => {
    let mounted = true;

    const loadWithdrawMethods = async () => {
      try {
        setPageLoading(true);
        setPageError("");

        const response = await axios.post("/apps-withdraw", {
          key_s: API_KEY,
        });

        if (!mounted) return;

        const methods = response?.data?.data;

        if (Array.isArray(methods)) {
          setWithdrawMethods(methods);
        } else {
          setWithdrawMethods([]);
        }
      } catch (error) {
        console.error("Withdraw methods load error:", error);

        if (!mounted) return;

        setPageError(
          error?.response?.data?.message ||
            "Unable to load withdrawal methods. Please try again."
        );
      } finally {
        if (mounted) {
          setPageLoading(false);
        }
      }
    };

    loadWithdrawMethods();

    return () => {
      mounted = false;
    };
  }, []);

  /* =========================================================
     SELECTED METHOD
  ========================================================= */

  const selectedMethod = useMemo(() => {
    if (!withdrawInput.gateway_id) return null;

    return (
      withdrawMethods.find(
        (method) =>
          String(method.id) === String(withdrawInput.gateway_id)
      ) || null
    );
  }, [withdrawMethods, withdrawInput.gateway_id]);

  /* =========================================================
     INPUT
  ========================================================= */

  const handleInput = (event) => {
    const { name } = event.target;
    let { value } = event.target;

    /*
     * Amount: allow only numbers and one decimal point.
     */
    if (name === "amount") {
      value = value.replace(/[^\d.]/g, "");

      const decimalParts = value.split(".");

      if (decimalParts.length > 2) {
        value = `${decimalParts[0]}.${decimalParts
          .slice(1)
          .join("")}`;
      }
    }

    setWithdrawInput((previous) => ({
      ...previous,
      [name]: value,
      error_list: {
        ...previous.error_list,
        [name]: "",
        ...(name === "gateway_id"
          ? { payment_method_name: "" }
          : {}),
      },
    }));
  };

  /* =========================================================
     CLIENT VALIDATION
  ========================================================= */

  const validateForm = () => {
    const errors = {};

    if (!withdrawInput.gateway_id) {
      errors.gateway_id = "Please select a withdrawal method.";
    }

    const amount = Number(withdrawInput.amount);

    if (!withdrawInput.amount) {
      errors.amount = "Withdrawal amount is required.";
    } else if (!Number.isFinite(amount) || amount <= 0) {
      errors.amount = "Please enter a valid withdrawal amount.";
    }

    if (!withdrawInput.transaction_number.trim()) {
      errors.transaction_number =
        "Receiver number / account information is required.";
    }

    if (!withdrawInput.password) {
      errors.password = "Your login password is required.";
    }

    setWithdrawInput((previous) => ({
      ...previous,
      error_list: errors,
    }));

    return Object.keys(errors).length === 0;
  };

  /* =========================================================
     SUBMIT
  ========================================================= */

  const withdrawSubmit = async (event) => {
    event.preventDefault();

    if (submitting) return;

    const authToken = localStorage.getItem("auth_token");

    if (!authToken) {
      swal(
        "Login Required",
        "Your session has expired. Please login again.",
        "warning"
      );
      return;
    }

    if (!validateForm()) {
      return;
    }

    const data = {
      gateway_id: withdrawInput.gateway_id,
      amount: withdrawInput.amount,
      transaction_number: withdrawInput.transaction_number.trim(),
      password: withdrawInput.password,
      w_select: withdrawInput.w_select,
      key_s: API_KEY,
    };

    try {
      setSubmitting(true);

      const response = await axios.post(
        "/apps-withdraw-sub",
        data
      );

      const result = response?.data || {};

      if (result.status === "success") {
        await swal(
          "Success",
          result.message || "Withdrawal request submitted successfully.",
          "success"
        );

        setWithdrawInput({
          gateway_id: "",
          amount: "",
          transaction_number: "",
          password: "",
          w_select: "",
          error_list: {},
        });

        setShowPassword(false);
      } else if (result.status === "fail") {
        swal(
          "Warning",
          result.message || "Unable to submit withdrawal request.",
          "warning"
        );
      } else if (result.validation_error) {
        setWithdrawInput((previous) => ({
          ...previous,
          error_list: result.validation_error || {},
        }));
      } else {
        swal(
          "Warning",
          result.message || "Something went wrong. Please try again.",
          "warning"
        );
      }
    } catch (error) {
      console.error("Withdraw submit error:", error);

      if (error?.response?.status === 422) {
        const validationErrors =
          error?.response?.data?.errors ||
          error?.response?.data?.validation_error ||
          {};

        setWithdrawInput((previous) => ({
          ...previous,
          error_list: validationErrors,
        }));

        return;
      }

      swal(
        "Error",
        error?.response?.data?.message ||
          "Unable to submit your withdrawal request. Please try again.",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  /* =========================================================
     ERRORS
  ========================================================= */

  const gatewayError =
    getErrorMessage(withdrawInput.error_list.gateway_id) ||
    getErrorMessage(
      withdrawInput.error_list.payment_method_name
    );

  const amountError = getErrorMessage(
    withdrawInput.error_list.amount
  );

  const transactionError = getErrorMessage(
    withdrawInput.error_list.transaction_number
  );

  const passwordError = getErrorMessage(
    withdrawInput.error_list.password
  );

  const canSubmit =
    Boolean(withdrawInput.gateway_id) &&
    Number(withdrawInput.amount) > 0 &&
    Boolean(withdrawInput.transaction_number.trim()) &&
    Boolean(withdrawInput.password) &&
    !pageLoading &&
    !submitting;

  /* =========================================================
     LOADING
  ========================================================= */

  /* Initial API loading is rendered only inside the withdrawal-method area. */

  /* =========================================================
     VIEW
  ========================================================= */

  return (
    <Fragment>
      <WithdrawStyles />

      <main className="wd-page" style={{background: "white",borderRadius: "20px"}}>
        <div className="wd-container">

          {/* =================================================
              ERROR
          ================================================= */}

          {pageError && (
            <div className="wd-page-error">
              <div className="wd-error-symbol">!</div>

              <div>
                <strong>Unable to load payment methods</strong>
                <p>{pageError}</p>
              </div>

              <button
                type="button"
                onClick={() => window.location.reload()}
              >
                Retry
              </button>
            </div>
          )}

          {/* =================================================
              MAIN GRID
          ================================================= */}

          <div className="wd-layout">

            {/* ===============================================
                FORM
            =============================================== */}

            <section className="wd-form-card">
              <div className="wd-card-heading">
                <div>
                  <span>Withdrawal Form</span>
                  <h2>Request Withdrawal</h2>
                  <p>
                    Complete the information below to continue.
                  </p>
                </div>

                <div className="wd-step-badge">
                  01
                </div>
              </div>

              <form
                onSubmit={withdrawSubmit}
                noValidate
                autoComplete="off"
              >

                {/* ===========================================
                    METHOD
                =========================================== */}

                <div className="wd-field">
                  <label htmlFor="withdraw-gateway">
                    {t("pro_with_w_me") || "Withdrawal Method"}
                    <b>*</b>
                  </label>

                  {pageLoading ? (
                    <div className="wd-method-loading">
                      <span className="wd-skeleton-box wd-sk-field-icon"></span>
                      <div className="wd-sk-method-content">
                        <span className="wd-skeleton-line wd-sk-method-text"></span>
                      </div>
                      <span className="wd-skeleton-line wd-sk-method-arrow"></span>
                    </div>
                  ) : (
                    <div
                      className={`wd-input-wrap ${
                        gatewayError ? "wd-has-error" : ""
                      }`}
                    >
                      <span className="wd-field-icon">
                        <WithdrawIcon type="method" />
                      </span>

                      <select
                        id="withdraw-gateway"
                        name="gateway_id"
                        value={withdrawInput.gateway_id}
                        onChange={handleInput}
                        disabled={
                          submitting ||
                          withdrawMethods.length === 0
                        }
                      >
                        <option value="">
                          {t("pro_with_sel_get") ||
                            "Select Withdrawal Method"}
                        </option>

                        {withdrawMethods.map((method) => (
                          <option
                            value={method.id}
                            key={method.id}
                          >
                            {method.payment_method_name}
                          </option>
                        ))}
                      </select>

                      <span className="wd-select-arrow">
                        ▾
                      </span>
                    </div>
                  )}

                  {gatewayError && (
                    <span className="wd-field-error">
                      {gatewayError}
                    </span>
                  )}

                  {!pageLoading &&
                    !pageError &&
                    withdrawMethods.length === 0 && (
                      <span className="wd-field-help">
                        No withdrawal methods are currently
                        available.
                      </span>
                    )}
                </div>

                {/* ===========================================
                    AMOUNT
                =========================================== */}

                <div className="wd-field">
                  <label htmlFor="withdraw-amount">
                    {t("pro_with_amount") || "Amount"}
                    <b>*</b>
                  </label>

                  <div
                    className={`wd-input-wrap ${
                      amountError ? "wd-has-error" : ""
                    }`}
                  >
                    <span className="wd-field-icon">
                      <WithdrawIcon type="amount" />
                    </span>

                    <input
                      id="withdraw-amount"
                      type="text"
                      inputMode="decimal"
                      name="amount"
                      placeholder="Enter withdrawal amount"
                      value={withdrawInput.amount}
                      onChange={handleInput}
                      disabled={submitting}
                    />
                  </div>

                  {amountError ? (
                    <span className="wd-field-error">
                      {amountError}
                    </span>
                  ) : (
                    <span className="wd-field-help">
                      Enter the amount you want to withdraw.
                    </span>
                  )}
                </div>

                {/* ===========================================
                    RECEIVER
                =========================================== */}

                <div className="wd-field">
                  <label htmlFor="withdraw-receiver">
                    {t("pro_with_tr_number") ||
                      "Receiver Number / Account"}
                    <b>*</b>
                  </label>

                  <div
                    className={`wd-input-wrap ${
                      transactionError ? "wd-has-error" : ""
                    }`}
                  >
                    <span className="wd-field-icon">
                      <WithdrawIcon type="receiver" />
                    </span>

                    <input
                      id="withdraw-receiver"
                      type="text"
                      name="transaction_number"
                      placeholder="Enter receiver number or account"
                      value={withdrawInput.transaction_number}
                      onChange={handleInput}
                      disabled={submitting}
                    />
                  </div>

                  {transactionError ? (
                    <span className="wd-field-error">
                      {transactionError}
                    </span>
                  ) : (
                    <span className="wd-field-help">
                      Make sure the receiver information is correct
                      before submitting.
                    </span>
                  )}
                </div>

                {/* ===========================================
                    PASSWORD
                =========================================== */}

                <div className="wd-field">
                  <label htmlFor="withdraw-password">
                    {t("pro_with_password") || "Login Password"}
                    <b>*</b>
                  </label>

                  <div
                    className={`wd-input-wrap ${
                      passwordError ? "wd-has-error" : ""
                    }`}
                  >
                    <span className="wd-field-icon">
                      <WithdrawIcon type="password" />
                    </span>

                    <input
                      id="withdraw-password"
                      type={
                        showPassword ? "text" : "password"
                      }
                      name="password"
                      placeholder="Enter your login password"
                      value={withdrawInput.password}
                      onChange={handleInput}
                      disabled={submitting}
                      autoComplete="current-password"
                    />

                    <button
                      type="button"
                      className="wd-password-toggle"
                      onClick={() =>
                        setShowPassword((previous) => !previous)
                      }
                      disabled={submitting}
                      aria-label={
                        showPassword
                          ? "Hide password"
                          : "Show password"
                      }
                    >
                      {showPassword ? "Hide" : "Show"}
                    </button>
                  </div>

                  {passwordError ? (
                    <span className="wd-field-error">
                      {passwordError}
                    </span>
                  ) : (
                    <span className="wd-field-help">
                      Your password is required to authorize this
                      withdrawal request.
                    </span>
                  )}
                </div>

                {/* ===========================================
                    SECURITY
                =========================================== */}

                <div className="wd-security-box">
                  <div>
                    <WithdrawIcon type="shield" />
                  </div>

                  <p>
                    Check your payment method, amount and receiver
                    information carefully. Incorrect receiver details
                    may cause the withdrawal to fail or be sent to the
                    wrong account.
                  </p>
                </div>

                {/* ===========================================
                    BUTTON
                =========================================== */}

                <button
                  type="submit"
                  className="wd-submit-btn"
                  disabled={
                    !canSubmit ||
                    withdrawMethods.length === 0
                  }
                >
                  {submitting ? (
                    <>
                      <span className="wd-button-spinner"></span>
                      Processing Request...
                    </>
                  ) : (
                    <>
                      <span>
                        {t("pro_with_withdraw") ||
                          "Submit Withdrawal"}
                      </span>

                      <span className="wd-button-arrow">
                        →
                      </span>
                    </>
                  )}
                </button>
              </form>
            </section>

            {/* ===============================================
                SUMMARY
            =============================================== */}

            <aside className="wd-summary-card">
              <div className="wd-summary-header">
                <span>Transaction Summary</span>
                <h2>Withdrawal Details</h2>
                <p>
                  Review your information before submitting.
                </p>
              </div>

              <div className="wd-method-preview">
                <div className="wd-preview-icon">
                  <WithdrawIcon type="method" />
                </div>

                <div>
                  <small>Payment Method</small>

                  {pageLoading ? (
                    <span className="wd-skeleton-line wd-sk-preview-value"></span>
                  ) : (
                    <strong>
                      {selectedMethod
                        ? safeText(
                            selectedMethod.payment_method_name
                          )
                        : "Not selected"}
                    </strong>
                  )}
                </div>
              </div>

              <div className="wd-summary-list">
                <div>
                  <span>Withdrawal Amount</span>

                  <strong
                    className={
                      withdrawInput.amount
                        ? "wd-summary-active"
                        : ""
                    }
                  >
                    {withdrawInput.amount
                      ? formatNumber(withdrawInput.amount)
                      : "0.00"}
                  </strong>
                </div>

                <div>
                  <span>Receiver</span>

                  <strong className="wd-summary-receiver">
                    {withdrawInput.transaction_number.trim()
                      ? withdrawInput.transaction_number.trim()
                      : "Not entered"}
                  </strong>
                </div>

                <div>
                  <span>Authorization</span>

                  <strong>
                    {withdrawInput.password
                      ? "Password entered"
                      : "Required"}
                  </strong>
                </div>

                <div>
                  <span>Request Status</span>

                  <strong className="wd-pending">
                    Pending Submission
                  </strong>
                </div>
              </div>

              <div className="wd-summary-total">
                <span>Requested Amount</span>

                <strong>
                  {withdrawInput.amount
                    ? formatNumber(withdrawInput.amount)
                    : "0.00"}
                </strong>
              </div>

              <div className="wd-info-note">
                <span>i</span>

                <p>
                  Withdrawal processing time and charges may depend
                  on the selected payment method and your account
                  rules.
                </p>
              </div>
            </aside>
          </div>
        </div>
      </main>
    </Fragment>
  );
};

/* =========================================================
   STYLES
========================================================= */

const WithdrawStyles = () => (
  <style>{`
    .wd-page {
      --wd-primary: #635bff;
      --wd-primary-dark: #5149db;
      --wd-text: #182230;
      --wd-muted: #667085;
      --wd-border: #e7e9ef;
      --wd-bg: #f7f8fb;
      --wd-danger: #d92d20;
      --wd-green: #12b76a;

      min-height: 100vh;
      padding: 6px 0 35px;
      background:
        radial-gradient(
          circle at 0 0,
          rgba(99, 91, 255, 0.055),
          transparent 27%
        ),
        var(--wd-bg);
      color: var(--wd-text);
      font-family:
        Inter,
        -apple-system,
        BlinkMacSystemFont,
        "Segoe UI",
        sans-serif;
    }

    .wd-container {
      width: 100%;
      margin: 0 auto;
    }

    /* =====================================================
       HERO
    ===================================================== */

    .wd-hero {
      position: relative;
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 30px;
      min-height: 205px;
      padding: 32px 35px;
      margin-bottom: 20px;
      border-radius: 23px;
      background:
        linear-gradient(
          135deg,
          #171b2d 0%,
          #252b48 56%,
          #393568 100%
        );
      box-shadow: 0 17px 45px rgba(31, 38, 65, 0.12);
    }

    .wd-hero-shape {
      position: absolute;
      border-radius: 50%;
      pointer-events: none;
    }

    .wd-shape-one {
      width: 330px;
      height: 330px;
      right: -115px;
      top: -195px;
      background: rgba(255, 255, 255, 0.065);
    }

    .wd-shape-two {
      width: 180px;
      height: 180px;
      right: 210px;
      bottom: -130px;
      background: rgba(99, 91, 255, 0.18);
    }

    .wd-hero-content {
      position: relative;
      z-index: 2;
      max-width: 680px;
    }

    .wd-eyebrow {
      display: block;
      margin-bottom: 7px;
      color: #c9c5ff;
      font-size: 13px;
      font-weight: 800;
      letter-spacing: 0.7px;
      text-transform: uppercase;
    }

    .wd-hero h1 {
      margin: 0 0 8px;
      color: #fff;
      font-size: 31px;
      font-weight: 850;
      line-height: 1.25;
    }

    .wd-hero p {
      margin: 0;
      color: rgba(255, 255, 255, 0.68);
      font-size: 15px;
      line-height: 1.65;
    }

    .wd-secure-badge {
      position: relative;
      z-index: 2;
      flex: 0 0 auto;
      min-width: 205px;
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 14px 16px;
      border: 1px solid rgba(255, 255, 255, 0.11);
      border-radius: 15px;
      background: rgba(255, 255, 255, 0.075);
      backdrop-filter: blur(8px);
    }

    .wd-secure-icon {
      width: 43px;
      height: 43px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 12px;
      background: rgba(117, 229, 170, 0.13);
      color: #75e5aa;
    }

    .wd-secure-badge small {
      display: block;
      margin-bottom: 2px;
      color: rgba(255, 255, 255, 0.52);
      font-size: 12px;
    }

    .wd-secure-badge strong {
      display: block;
      color: #fff;
      font-size: 15px;
      font-weight: 800;
    }

    /* =====================================================
       LAYOUT
    ===================================================== */

    .wd-layout {
      display: grid;
      grid-template-columns:
        minmax(0, 1.45fr)
        minmax(320px, 0.65fr);
      gap: 20px;
      align-items: start;
    }

    .wd-form-card,
    .wd-summary-card {
      border: 1px solid var(--wd-border);
      border-radius: 21px;
      background: #fff;
      box-shadow: 0 8px 30px rgba(30, 38, 64, 0.05);
    }

    .wd-form-card {
      padding: 25px;
    }

    .wd-summary-card {
      position: sticky;
      top: 20px;
      padding: 22px;
    }

    /* =====================================================
       HEADING
    ===================================================== */

    .wd-card-heading {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 20px;
      padding-bottom: 19px;
      margin-bottom: 22px;
      border-bottom: 1px solid #eef0f3;
    }

    .wd-card-heading > div:first-child > span,
    .wd-summary-header > span {
      display: block;
      margin-bottom: 4px;
      color: var(--wd-primary);
      font-size: 12px;
      font-weight: 800;
      letter-spacing: 0.6px;
      text-transform: uppercase;
    }

    .wd-card-heading h2,
    .wd-summary-header h2 {
      margin: 0 0 5px;
      color: var(--wd-text);
      font-size: 22px;
      font-weight: 850;
      line-height: 1.3;
    }

    .wd-card-heading p,
    .wd-summary-header p {
      margin: 0;
      color: var(--wd-muted);
      font-size: 14px;
      line-height: 1.55;
    }

    .wd-step-badge {
      flex: 0 0 45px;
      width: 45px;
      height: 45px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 13px;
      background: #efedff;
      color: var(--wd-primary);
      font-size: 14px;
      font-weight: 900;
    }

    /* =====================================================
       FORM
    ===================================================== */

    .wd-field {
      margin-bottom: 20px;
    }

    .wd-field > label {
      display: block;
      margin-bottom: 8px;
      color: #344054;
      font-size: 15px;
      font-weight: 750;
    }

    .wd-field > label b {
      margin-left: 4px;
      color: #f04438;
      font-weight: 800;
    }

    .wd-input-wrap {
      position: relative;
      display: flex;
      align-items: center;
      min-height: 55px;
      border: 1px solid #dfe2e8;
      border-radius: 13px;
      background: #fff;
      transition:
        border-color 0.2s ease,
        box-shadow 0.2s ease;
    }

    .wd-input-wrap:focus-within {
      border-color: var(--wd-primary);
      box-shadow: 0 0 0 4px rgba(99, 91, 255, 0.09);
    }

    .wd-input-wrap.wd-has-error {
      border-color: #f97066;
    }

    .wd-field-icon {
      flex: 0 0 51px;
      width: 51px;
      height: 53px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--wd-primary);
      border-right: 1px solid #eef0f3;
    }

    .wd-input-wrap input,
    .wd-input-wrap select {
      width: 100%;
      min-width: 0;
      height: 53px;
      padding: 0 15px;
      border: 0;
      outline: 0;
      background: transparent;
      color: var(--wd-text);
      font-family: inherit;
      font-size: 15px;
      font-weight: 600;
    }

    .wd-input-wrap input::placeholder {
      color: #a3aab7;
      font-weight: 500;
    }

    .wd-input-wrap select {
      padding-right: 42px;
      appearance: none;
      cursor: pointer;
    }

    .wd-input-wrap input:disabled,
    .wd-input-wrap select:disabled {
      cursor: not-allowed;
      opacity: 0.65;
    }

    .wd-select-arrow {
      position: absolute;
      right: 15px;
      top: 50%;
      transform: translateY(-50%);
      color: #667085;
      font-size: 17px;
      pointer-events: none;
    }

    .wd-password-toggle {
      flex: 0 0 auto;
      margin-right: 8px;
      padding: 7px 10px;
      border: 0;
      border-radius: 8px;
      background: #f2f1ff;
      color: var(--wd-primary);
      font-family: inherit;
      font-size: 12px;
      font-weight: 800;
      cursor: pointer;
    }

    .wd-password-toggle:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }

    .wd-field-error,
    .wd-field-help {
      display: block;
      margin-top: 6px;
      font-size: 13px;
      line-height: 1.5;
    }

    .wd-field-error {
      color: var(--wd-danger);
      font-weight: 650;
    }

    .wd-field-help {
      color: #7d8695;
    }

    /* =====================================================
       SECURITY BOX
    ===================================================== */

    .wd-security-box {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      padding: 14px;
      margin: 4px 0 20px;
      border: 1px solid #d9f1e4;
      border-radius: 13px;
      background: #f4fbf7;
    }

    .wd-security-box > div {
      flex: 0 0 35px;
      width: 35px;
      height: 35px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 10px;
      background: #dcf8e8;
      color: #079455;
    }

    .wd-security-box p {
      margin: 0;
      color: #527060;
      font-size: 13px;
      line-height: 1.65;
    }

    /* =====================================================
       SUBMIT
    ===================================================== */

    .wd-submit-btn {
      width: 100%;
      min-height: 55px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      padding: 12px 20px;
      border: 0;
      border-radius: 13px;
      background:
        linear-gradient(
          135deg,
          var(--wd-primary),
          #766fff
        );
      color: #fff;
      font-family: inherit;
      font-size: 15px;
      font-weight: 850;
      cursor: pointer;
      box-shadow: 0 10px 22px rgba(99, 91, 255, 0.19);
      transition:
        transform 0.2s ease,
        box-shadow 0.2s ease,
        opacity 0.2s ease;
    }

    .wd-submit-btn:not(:disabled):hover {
      transform: translateY(-1px);
      box-shadow: 0 13px 28px rgba(99, 91, 255, 0.24);
    }

    .wd-submit-btn:disabled {
      opacity: 0.52;
      cursor: not-allowed;
      box-shadow: none;
    }

    .wd-button-arrow {
      font-size: 19px;
    }

    .wd-button-spinner {
      width: 19px;
      height: 19px;
      border: 2px solid rgba(255, 255, 255, 0.35);
      border-top-color: #fff;
      border-radius: 50%;
      animation: wd-spin 0.7s linear infinite;
    }

    @keyframes wd-spin {
      to {
        transform: rotate(360deg);
      }
    }

    /* =====================================================
       SUMMARY
    ===================================================== */

    .wd-summary-header {
      padding-bottom: 17px;
      margin-bottom: 16px;
      border-bottom: 1px solid #eef0f3;
    }

    .wd-method-preview {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 14px;
      margin-bottom: 13px;
      border: 1px solid #ebe9ff;
      border-radius: 13px;
      background: #f8f7ff;
    }

    .wd-preview-icon {
      flex: 0 0 42px;
      width: 42px;
      height: 42px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 11px;
      background: #e9e7ff;
      color: var(--wd-primary);
    }

    .wd-method-preview small {
      display: block;
      margin-bottom: 2px;
      color: #7d8695;
      font-size: 12px;
    }

    .wd-method-preview strong {
      display: block;
      color: var(--wd-text);
      font-size: 14px;
      font-weight: 800;
      overflow-wrap: anywhere;
    }

    .wd-summary-list > div {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 15px;
      padding: 13px 1px;
      border-bottom: 1px solid #eef0f3;
    }

    .wd-summary-list span {
      color: var(--wd-muted);
      font-size: 13px;
      line-height: 1.45;
    }

    .wd-summary-list strong {
      max-width: 55%;
      color: #344054;
      font-size: 13px;
      font-weight: 800;
      line-height: 1.45;
      text-align: right;
      overflow-wrap: anywhere;
    }

    .wd-summary-list strong.wd-summary-active {
      color: var(--wd-primary);
      font-size: 15px;
    }

    .wd-summary-list strong.wd-pending {
      color: #b54708;
    }

    .wd-summary-total {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 15px;
      padding: 16px;
      margin-top: 15px;
      border-radius: 13px;
      background:
        linear-gradient(
          135deg,
          #1c2136,
          #30365b
        );
    }

    .wd-summary-total span {
      color: rgba(255, 255, 255, 0.65);
      font-size: 13px;
    }

    .wd-summary-total strong {
      color: #fff;
      font-size: 20px;
      font-weight: 900;
      overflow-wrap: anywhere;
    }

    .wd-info-note {
      display: flex;
      align-items: flex-start;
      gap: 9px;
      padding: 12px;
      margin-top: 14px;
      border-radius: 11px;
      background: #f6f7f9;
    }

    .wd-info-note > span {
      flex: 0 0 24px;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      background: #e9e7ff;
      color: var(--wd-primary);
      font-size: 12px;
      font-weight: 900;
    }

    .wd-info-note p {
      margin: 0;
      color: #667085;
      font-size: 12px;
      line-height: 1.6;
    }

    /* =====================================================
       AREA-BASED API LOADING
       The page remains visible. Only API-dependent method data
       displays a lightweight skeleton while loading.
    ===================================================== */

    .wd-skeleton-line,
    .wd-skeleton-box {
      display: block;
      position: relative;
      overflow: hidden;
      background: #eef0f4;
    }

    .wd-skeleton-line {
      border-radius: 999px;
    }

    .wd-skeleton-box {
      border-radius: 11px;
    }

    .wd-skeleton-line::after,
    .wd-skeleton-box::after {
      content: "";
      position: absolute;
      inset: 0;
      transform: translateX(-100%);
      background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.82),
        transparent
      );
      animation: wd-local-shimmer 1.25s infinite;
    }

    @keyframes wd-local-shimmer {
      100% {
        transform: translateX(100%);
      }
    }

    .wd-method-loading {
      display: flex;
      align-items: center;
      width: 100%;
      min-height: 55px;
      overflow: hidden;
      border: 1px solid #dfe2e8;
      border-radius: 13px;
      background: #fff;
    }

    .wd-sk-field-icon {
      flex: 0 0 51px;
      width: 51px;
      height: 53px;
      border-radius: 0;
      border-right: 1px solid #eef0f3;
      background: #f2f3f6;
    }

    .wd-sk-method-content {
      flex: 1;
      padding: 0 15px;
    }

    .wd-sk-method-text {
      width: min(230px, 65%);
      height: 16px;
    }

    .wd-sk-method-arrow {
      flex: 0 0 16px;
      width: 16px;
      height: 16px;
      margin-right: 16px;
    }

    .wd-sk-preview-value {
      width: 125px;
      max-width: 100%;
      height: 15px;
      margin-top: 5px;
    }

    /* =====================================================
       PAGE ERROR
    ===================================================== */

    .wd-page-error {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 14px 16px;
      margin-bottom: 18px;
      border: 1px solid #fecdca;
      border-radius: 13px;
      background: #fff6f5;
    }

    .wd-error-symbol {
      flex: 0 0 34px;
      width: 34px;
      height: 34px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 10px;
      background: #f04438;
      color: #fff;
      font-size: 15px;
      font-weight: 900;
    }

    .wd-page-error > div:nth-child(2) {
      flex: 1;
    }

    .wd-page-error strong {
      display: block;
      margin-bottom: 2px;
      color: #b42318;
      font-size: 14px;
      font-weight: 800;
    }

    .wd-page-error p {
      margin: 0;
      color: #d92d20;
      font-size: 13px;
    }

    .wd-page-error button {
      padding: 8px 12px;
      border: 0;
      border-radius: 9px;
      background: #f04438;
      color: #fff;
      font-size: 12px;
      font-weight: 800;
      cursor: pointer;
    }

    /* =====================================================
       LOADING
    ===================================================== */

    .wd-loading-card {
      max-width: 520px;
      margin: 70px auto;
      padding: 40px 25px;
      border: 1px solid var(--wd-border);
      border-radius: 21px;
      background: #fff;
      text-align: center;
      box-shadow: 0 10px 35px rgba(30, 38, 64, 0.06);
    }

    .wd-spinner {
      width: 45px;
      height: 45px;
      margin: 0 auto 16px;
      border: 4px solid #e4e2ff;
      border-top-color: var(--wd-primary);
      border-radius: 50%;
      animation: wd-spin 0.75s linear infinite;
    }

    .wd-loading-card h2 {
      margin: 0 0 7px;
      color: var(--wd-text);
      font-size: 21px;
      font-weight: 850;
    }

    .wd-loading-card p {
      margin: 0;
      color: var(--wd-muted);
      font-size: 14px;
      line-height: 1.6;
    }

    /* =====================================================
       TABLET
    ===================================================== */

    @media (max-width: 1050px) {
      .wd-layout {
        grid-template-columns: 1fr;
      }

      .wd-summary-card {
        position: static;
      }
    }

    /* =====================================================
       MOBILE
    ===================================================== */

    @media (max-width: 650px) {
      .wd-page {
        padding-bottom: 20px;
      }

      .wd-hero {
        min-height: auto;
        flex-direction: column;
        align-items: flex-start;
        padding: 23px 18px;
        border-radius: 18px;
      }

      .wd-hero h1 {
        font-size: 24px;
      }

      .wd-hero p {
        font-size: 14px;
      }

      .wd-eyebrow {
        font-size: 12px;
      }

      .wd-secure-badge {
        width: 100%;
        min-width: 0;
      }

      .wd-form-card,
      .wd-summary-card {
        padding: 17px;
        border-radius: 17px;
      }

      .wd-card-heading h2,
      .wd-summary-header h2 {
        font-size: 20px;
      }

      .wd-card-heading p,
      .wd-summary-header p {
        font-size: 13px;
      }

      .wd-step-badge {
        flex-basis: 40px;
        width: 40px;
        height: 40px;
      }

      .wd-field > label {
        font-size: 14px;
      }

      .wd-input-wrap {
        min-height: 53px;
      }

      .wd-field-icon {
        flex-basis: 47px;
        width: 47px;
        height: 51px;
      }

      .wd-input-wrap input,
      .wd-input-wrap select {
        height: 51px;
        padding-left: 12px;
        font-size: 14px;
      }

      .wd-field-help,
      .wd-field-error {
        font-size: 12px;
      }

      .wd-security-box p {
        font-size: 12px;
      }

      .wd-submit-btn {
        min-height: 53px;
        font-size: 14px;
      }

      .wd-page-error {
        align-items: flex-start;
        flex-wrap: wrap;
      }
    }

    @media (max-width: 390px) {
      .wd-card-heading {
        gap: 10px;
      }

      .wd-step-badge {
        display: none;
      }

      .wd-summary-list > div {
        flex-direction: column;
        gap: 4px;
      }

      .wd-summary-list strong {
        max-width: 100%;
        text-align: left;
      }
    }
  `}</style>
);

export default Checkout;
